import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";

import { selectCurrentUser } from "../../../store/slices/currentUserSlice";
import {
  ValidationResultInterface,
  ValidationRulesInterface,
} from "../../../common/types/interfaces";

const MAX_COMMENT_LENGTH = 200;
const CUTLERY_MIN = 0;
const CUTLERY_MAX = 10;
const CUTLERY_STEP = 1;

const orderDetailsValidations: ValidationRulesInterface[] = [
  {
    fieldName: "phone",
    minLength: 10,
    maxLength: 15,
    canBeEmpty: false,
    isEmptyMessage: "Телефон обязателен",
    toShortMessage: "Слишком короткий номер",
    tooLongMessage: "Слишком длинный номер",
    format: /^\+?\d+$/,
    formatMessage: "Только цифры и + в начале",
  },
  {
    fieldName: "comment",
    maxLength: MAX_COMMENT_LENGTH,
    canBeEmpty: true,
    tooLongMessage: `Максимум ${MAX_COMMENT_LENGTH} символов`,
    isEmptyMessage: "",
    toShortMessage: "",
    formatMessage: "",
  },
];

const buildResult = (
  rules: ValidationRulesInterface,
  value: string,
  isDirty: boolean,
): ValidationResultInterface => {
  const isEmpty = value.length === 0;
  const tooShort =
    !!rules.minLength && value.length > 0 && value.length < rules.minLength;
  const tooLong = !!rules.maxLength && value.length > rules.maxLength;
  const notValidFormat =
    !!rules.format && value.length > 0 && !rules.format.test(value);
  const isEmptyError = isEmpty && rules.canBeEmpty === false;

  let errorMessage = "";
  if (isEmptyError) errorMessage = rules.isEmptyMessage;
  else if (tooShort) errorMessage = rules.toShortMessage;
  else if (tooLong) errorMessage = rules.tooLongMessage;
  else if (notValidFormat) errorMessage = rules.formatMessage;

  return {
    name: rules.fieldName,
    isDirty,
    isEmpty,
    notValidFormat,
    tooShort,
    tooLong,
    isEmptyError,
    notValid: isEmptyError || tooShort || tooLong || notValidFormat,
    errorMessage,
  };
};

export const useOrderDetails = () => {
  const currentUser = useSelector(selectCurrentUser);

  const [data, setData] = useState({
    phone: currentUser?.phone_number || "",
    comment: "",
  });

  const [dirty, setDirtyState] = useState<Record<string, boolean>>({});
  const [cutleryCount, setCutleryCount] = useState<number>(CUTLERY_MIN);
  const [validationResult, setValidationResult] = useState<
    ValidationResultInterface[]
  >([]);
  const [isValid, setIsValid] = useState(false);

  const inputHandler = (value: string, fieldName: "phone" | "comment") => {
    setData((prev) => ({ ...prev, [fieldName]: value }));
    setDirtyState((prev) => ({ ...prev, [fieldName]: true }));
  };

  const getValidationResult = (fieldName: string) =>
    validationResult.find((item) => item.name === fieldName);

  const setDirty = useCallback((fieldName: string) => {
    setDirtyState((prev) => ({ ...prev, [fieldName]: true }));
  }, []);

  const incrementCutlery = () =>
    setCutleryCount((prev) => Math.min(prev + CUTLERY_STEP, CUTLERY_MAX));

  const decrementCutlery = () =>
    setCutleryCount((prev) => Math.max(prev - CUTLERY_STEP, CUTLERY_MIN));

  useEffect(() => {
    const results = orderDetailsValidations.map((rules) => {
      const value = (data as any)[rules.fieldName] || "";
      return buildResult(rules, value, !!dirty[rules.fieldName]);
    });

    setValidationResult(results);
    setIsValid(results.every((item) => !item.notValid));
  }, [data, dirty]);

  return {
    phone: data.phone,
    comment: data.comment,
    getValidationResult,
    inputHandler,
    setDirty,
    isValid,
    MAX_COMMENT_LENGTH,
    cutleryCount,
    setCutleryCount,
    incrementCutlery,
    decrementCutlery,
  };
};
